import { useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import recaptchaProps from './recaptcha-props';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { ErrorMessage, SuccessMessage, FailMessage, LoaderImage } from './shared-form-components';

const schema = yup.object({
  first_name: yup.string().required(),
  last_name: yup.string().required(),
  email: yup.string().email().required(),
  what_are_you_looking_for: yup.string().required().min(10)
});

const RequestProposalFormBody = ({ layoutClasses = 'p-4 mt-4 mb-5' }) => {
  const [formState, setFormState] = useState('default');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  const { executeRecaptcha } = useGoogleReCaptcha();

  const onSubmit = async (data) => {
    setFormState('submitting');
    if (!executeRecaptcha) {
      return;
    }

    const token = await executeRecaptcha('request_proposal_form');
    data.recaptchaToken = token; // handle verification on backend with secret key

    try {
      const response = await fetch(`${process.env.API}/contact/request-proposal`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });

      if (response.ok) {
        setFormState('success');
        reset();
      } else {
        setFormState('error');
      }
    } catch (error) {
      setFormState('error');
      console.error(error);
    }
  };

  return (
    <div className={`${layoutClasses} background-plum rounded`}>
      {formState === 'success' && <SuccessMessage />}

      {formState === 'submitting' && <LoaderImage />}

      {(formState === 'default' || formState === 'error') && (
        <>
          <p className="h3 text-white">Request A Proposal</p>
          <p className="text-white">
            We believe that every student and recent grad deserves a great career. Get a
            personalized proposal for how best to reach your target candidates by entering your info
            here. We will respond within one business day.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="styled proposal" action="">
            <fieldset>
              <legend>Name</legend>
              <div className="row gx-2">
                <div className="col-sm-6 pb-3">
                  <input {...register('first_name')} id="first-name" aria-required="false" />
                  <label htmlFor="first-name">First</label>
                  {errors.first_name?.type === 'required' && (
                    <ErrorMessage message="First name is required" />
                  )}
                </div>
                <div className="col-sm-6 pb-3">
                  <input {...register('last_name')} id="last-name" aria-required="false" />
                  <label htmlFor="last-name">Last</label>
                  {errors.last_name?.type === 'required' && (
                    <ErrorMessage message="Last name is required" />
                  )}
                </div>
              </div>
            </fieldset>

            <div className="pb-4">
              <label htmlFor="email">Email</label>
              <input
                {...register('email', { required: true })}
                id="email"
                type="email"
                aria-invalid={errors.email ? 'true' : 'false'}
              />
              {errors.email?.type === 'required' && <ErrorMessage message="Email is required" />}
              {errors.email?.type === 'email' && (
                <ErrorMessage message="Please enter a valid email address" />
              )}
            </div>
            <div className="pb-4">
              <label htmlFor="looking-for">What are you looking for?</label>
              <select
                {...register('what_are_you_looking_for', { required: true })}
                id="looking-for"
                type="text"
                aria-invalid={errors.what_are_you_looking_for}
              >
                <option value="" disabled="">
                  Select a product
                </option>
                <option value="Targeted Email Campaigns">Targeted Email Campaigns</option>
                <option value="JobsThatScale">JobsThatScale™ Postings</option>
                <option value="Standard Postings">Standard Postings</option>
              </select>
              {errors.what_are_you_looking_for?.type === 'required' && (
                <ErrorMessage message="Message is required" />
              )}
              {errors.what_are_you_looking_for?.type === 'min' && (
                <ErrorMessage message="Message must be at least 10 characters long" />
              )}
            </div>
            <div
              className="g-recaptcha"
              data-sitekey="6LcI8SwUAAAAAGMWFmsE-fFUhg-vMByG0h9g-BK3"
            ></div>

            {formState === 'error' && <FailMessage />}

            <div className="pb-4">
              {(formState === 'default' || formState === 'error') && (
                <input className="text-btn" type="submit" value="Submit" />
              )}
              {formState === 'submitting' && (
                <input className="text-btn" type="submit" value="Submitting ..." disabled />
              )}
              {formState === 'success' && (
                <input className="text-btn" type="submit" value="Success!" disabled />
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
};

const RequestProposalForm = () => {
  return (
    <GoogleReCaptchaProvider {...recaptchaProps}>
      <RequestProposalFormBody />
    </GoogleReCaptchaProvider>
  );
};

module.exports = RequestProposalForm;
